const STORAGE_KEY = 'liore-theme';

/**
 * <theme-toggle> — switches the storefront between light and dark palettes.
 * The initial theme is applied by an inline script in <head> (no flash);
 * this component handles user interaction and persistence.
 */
class ThemeToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.handleClick = this.handleClick.bind(this);
    this.button.addEventListener('click', this.handleClick);

    // Keep multiple toggles (e.g. desktop + drawer) in sync.
    this.handleThemeChange = this.handleThemeChange.bind(this);
    document.addEventListener('liore:theme-change', this.handleThemeChange);

    this.sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
    document.removeEventListener('liore:theme-change', this.handleThemeChange);
  }

  current() {
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  }

  handleClick() {
    const next = this.current() === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      /* storage unavailable — preference simply won't persist */
    }
    document.dispatchEvent(new CustomEvent('liore:theme-change', { detail: { theme: next } }));
  }

  handleThemeChange() {
    this.sync();
  }

  sync() {
    const isDark = this.current() === 'dark';
    this.button.setAttribute('aria-pressed', String(isDark));
  }
}

if (!customElements.get('theme-toggle')) {
  customElements.define('theme-toggle', ThemeToggle);
}
